import React, { useMemo } from 'react';
import { View } from 'react-native';
import moment from 'moment';
import { dotTimeIndicatorColor, machineStatusColorName, machineTimeType } from './functions';
import TimelineWithTimeLabels from './timelineWithTimeLabels';
import LinePointWrapper from './linePointWrapper';
import DotIndicator from './dotIndicator';
import { getTargetTimeAndType, getMachineDataByStatus } from '@components/wallboard/helpers/productivityTimeline';

export default ({ timeOffset, lastUpdatedOn, status, C, scale, font, lineHeight = 5, convertTime, batch, tourId }) => {
  // Get duration data for this machine status (setup, active, change)
  const machineData = getMachineDataByStatus(batch, status);
  const { idealTimeInSeconds, paidTimeInSeconds, scheduledTimeInSeconds } = machineData;

  // Select target time and its type (scheduled > paid > ideal)
  const { targetTime, targetTimeType } = getTargetTimeAndType(machineData);

  // Calculate running time (in seconds) based on current time and last update
  const time = moment.utc().add(timeOffset, 'milliseconds');
  const runningTime = time.diff(moment.utc(lastUpdatedOn), 'seconds');

  // Determine the max duration to use as 100% for scaling
  const maxDuration = Math.max(idealTimeInSeconds, paidTimeInSeconds, scheduledTimeInSeconds, runningTime);

  // Flags for rendering logic
  const isTargetDuration = Boolean(targetTime);
  const hasExceededTarget = isTargetDuration && runningTime > targetTime;

  // Base raw percent calculator (no safety checks)
  const getPercent = (value) => (value * 100) / maxDuration;

  // Safe wrapper: returns 0 if maxDuration is zero or undefined
  const getSafePercent = (value) => (maxDuration > 0 ? getPercent(value) : 0);

  const runningPercent = getSafePercent(runningTime);
  const targetPercent = getSafePercent(targetTime);

  // Green bar width: from 0 to target (or running time if no target)
  const greenWidth = isTargetDuration ? `${getSafePercent(Math.min(runningTime, targetTime))}%` : `${runningPercent}%`;

  // Red bar width: only shown if running time exceeds the target
  const redWidth = hasExceededTarget ? `${getSafePercent(runningTime - targetTime)}%` : '0%';
  const redLeft = `${targetPercent}%`; // Red bar starts where target ends

  // Calculate overtime or remaining time for label
  const isOvertime = isTargetDuration && hasExceededTarget;
  const isRemaining = isTargetDuration && !hasExceededTarget;
  const overtimeTime = isOvertime ? runningTime - targetTime : null;
  const remainingTime = isRemaining ? -(targetTime - runningTime) : null;
  const remainingOrExceededTime = isOvertime ? overtimeTime : remainingTime;

  const targetTimeIndicatorColor = dotTimeIndicatorColor?.[targetTimeType];

  const timeLabel = useMemo(
    () => [
      { value: runningTime },
      {
        value: targetTime === 0 ? null : targetTime,
        Indicator: <DotIndicator {...{ scale, C, size: 4, color: targetTimeIndicatorColor, hasOutline: false }} />,
      },
      {
        value: remainingOrExceededTime,
        color: hasExceededTarget ? 'Label/Light/Warning' : undefined,
        showPlus: true,
      },
    ],
    [runningTime, targetTime, remainingOrExceededTime, hasExceededTarget, scale, C, targetTimeIndicatorColor]
  );

  const allTimePoints = useMemo(
    () => [
      {
        positionPercent: targetPercent,
        pointComponent: () => <DotIndicator {...{ scale, C, color: dotTimeIndicatorColor[machineTimeType.SCHEDULED] }} />,
      },
      {
        positionPercent: getPercent(idealTimeInSeconds),
        pointComponent: () => <DotIndicator {...{ scale, C, color: dotTimeIndicatorColor[machineTimeType.IDEAL] }} />,
      },
      {
        positionPercent: getPercent(paidTimeInSeconds),
        pointComponent: () => <DotIndicator {...{ scale, C, color: dotTimeIndicatorColor[machineTimeType.PAID] }} />,
      },
    ],
    [targetPercent, idealTimeInSeconds, paidTimeInSeconds, maxDuration, scale, C]
  );

  return (
    <TimelineWithTimeLabels values={timeLabel} C={C} font={font} scale={scale} convertTime={convertTime} tourId={tourId}>
      <LinePointWrapper scale={scale} points={allTimePoints}>
        <View
          style={{
            backgroundColor: C('Background/Light/Chart No Trans'),
            width: `100%`,
            height: lineHeight,
            borderRadius: lineHeight / 2,
            overflow: 'hidden',
          }}
        >
          {isTargetDuration && (
            <>
              <View
                style={{
                  backgroundColor: C(machineStatusColorName[status]),
                  width: greenWidth,
                  height: lineHeight,
                }}
              />

              {hasExceededTarget && (
                <View
                  style={{
                    position: 'absolute',
                    left: redLeft,
                    width: redWidth,
                    backgroundColor: C('Label/Light/Warning'),
                    height: lineHeight,
                  }}
                />
              )}
            </>
          )}
        </View>
      </LinePointWrapper>
    </TimelineWithTimeLabels>
  );
};
